import { api, ApiError } from "./api.js";
import { esc, displayName, toast } from "./utils.js";
import { getCurrentUser } from "./auth.js";

/** Must match Reaction.EMOJI in app/models.py — the API rejects anything else. */
export const EMOJI = ["👍", "❤️", "😂", "😮", "🤿"];

/**
 * The reaction bar for a post or comment.
 *
 * `target` is {postId} or {postId, commentId}; the comment endpoints are
 * nested under their post, so both ids are needed either way.
 *
 * Chips toggle your own reaction. The trailing total opens the list of
 * who reacted — kept as a separate control because tapping a chip already
 * means "react", and overloading it would make one of the two actions
 * unreachable.
 */
export function reactionsBarHtml(target, reactions) {
  const block = reactions || { counts: {}, total: 0, mine: null };
  const key = target.commentId ? `c:${target.postId}:${target.commentId}` : `p:${target.postId}`;
  const signedIn = getCurrentUser() != null;

  const chips = EMOJI.filter((e) => block.counts[e]).map((emoji) => {
    const mine = block.mine === emoji;
    return `
      <button type="button"
              class="btn btn-sm ${mine ? "btn-primary" : "btn-light border"} reaction-chip"
              data-emoji="${emoji}" data-mine="${mine}"
              ${signedIn ? "" : "disabled"}
              aria-pressed="${mine}">
        ${emoji} <span class="ms-1">${block.counts[emoji]}</span>
      </button>`;
  });

  // Nothing to show and nothing to do: render an empty bar rather than a
  // lone "+" on every post in a quiet feed.
  if (!chips.length && !signedIn) return "";

  return `
    <div class="d-flex align-items-center gap-1 flex-wrap reactions" data-reaction-key="${key}">
      ${chips.join("")}
      ${signedIn
        ? `<button type="button" class="btn btn-sm btn-light border reaction-add" title="Add a reaction" aria-label="Add a reaction">
             <i class="bi bi-emoji-smile"></i>
           </button>`
        : ""}
      ${block.total
        ? `<button type="button" class="btn btn-sm btn-link text-slate p-0 ms-1 reaction-who">
             ${block.total} reaction${block.total === 1 ? "" : "s"}
           </button>`
        : ""}
    </div>`;
}

function parseKey(key) {
  const [kind, postId, commentId] = key.split(":");
  return kind === "c"
    ? { postId: Number(postId), commentId: Number(commentId) }
    : { postId: Number(postId) };
}

function endpoint({ postId, commentId }, suffix) {
  return commentId
    ? `/posts/${postId}/comments/${commentId}/${suffix}`
    : `/posts/${postId}/${suffix}`;
}

/** Rewrites one bar in place from a fresh reaction payload. */
function repaint(bar, detail, mine) {
  const target = parseKey(bar.dataset.reactionKey);
  const counts = {};
  for (const group of detail.groups || []) counts[group.emoji] = group.count;
  const replacement = reactionsBarHtml(target, { counts, total: detail.total, mine });
  if (!replacement) {
    bar.replaceWith(document.createRange().createContextualFragment(""));
    return null;
  }
  const fragment = document.createRange().createContextualFragment(replacement);
  const fresh = fragment.firstElementChild;
  bar.replaceWith(fresh);
  return fresh;
}

async function toggle(bar, emoji) {
  const target = parseKey(bar.dataset.reactionKey);
  const chip = bar.querySelector(`.reaction-chip[data-emoji="${emoji}"]`);
  const clearing = chip?.dataset.mine === "true";

  bar.querySelectorAll("button").forEach((b) => (b.disabled = true));
  try {
    const detail = clearing
      ? await api.delete(endpoint(target, "reaction"))
      // PUT, not POST: setting a reaction replaces whatever you had, and
      // the server route is declared that way. Calling post() here was a
      // 405 waiting to happen.
      : await api.put(endpoint(target, "reaction"), { emoji });
    const fresh = repaint(bar, detail, clearing ? null : emoji);
    if (fresh) bindBar(fresh);
  } catch (err) {
    bar.querySelectorAll("button").forEach((b) => (b.disabled = false));
    toast(err instanceof ApiError ? err.message : "Could not save that reaction.", "danger");
  }
}

function showPicker(bar, anchor) {
  document.querySelectorAll(".reaction-picker").forEach((el) => el.remove());

  const picker = document.createElement("div");
  picker.className = "reaction-picker card shadow-sm p-1 d-flex flex-row gap-1";
  picker.style.cssText = "position:absolute;z-index:1080;";
  picker.innerHTML = EMOJI.map(
    (e) => `<button type="button" class="btn btn-sm btn-light" data-pick="${e}">${e}</button>`
  ).join("");
  document.body.appendChild(picker);

  const rect = anchor.getBoundingClientRect();
  picker.style.left = `${window.scrollX + rect.left}px`;
  picker.style.top = `${window.scrollY + rect.bottom + 4}px`;

  picker.querySelectorAll("[data-pick]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const emoji = btn.dataset.pick;
      picker.remove();
      toggle(bar, emoji);
    });
  });

  // Dismiss on the next click anywhere else. Deferred by a tick so the
  // click that opened it doesn't immediately close it.
  setTimeout(() => {
    const close = (e) => {
      if (!picker.contains(e.target)) {
        picker.remove();
        document.removeEventListener("click", close);
      }
    };
    document.addEventListener("click", close);
  }, 0);
}

async function showWhoReacted(bar) {
  const target = parseKey(bar.dataset.reactionKey);
  let detail;
  try {
    detail = await api.get(endpoint(target, "reactions"));
  } catch (err) {
    toast(err instanceof ApiError ? err.message : "Could not load reactions.", "danger");
    return;
  }

  const body = (detail.groups || [])
    .map(
      (group) => `
      <div class="mb-3">
        <div class="fw-semibold mb-1">${group.emoji} <span class="text-slate small">${group.count}</span></div>
        <div class="text-slate small">${group.users.map((u) => esc(displayName(u))).join(", ")}${
          group.truncated ? ", and more" : ""
        }</div>
      </div>`
    )
    .join("");

  const host = document.createElement("div");
  host.innerHTML = `
    <div class="modal fade" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Reactions</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">${body || `<p class="text-slate mb-0">No reactions yet.</p>`}</div>
        </div>
      </div>
    </div>`;
  const el = host.firstElementChild;
  document.body.appendChild(el);
  const modal = new bootstrap.Modal(el);
  el.addEventListener("hidden.bs.modal", () => el.remove());
  modal.show();
}

function bindBar(bar) {
  bar.querySelectorAll(".reaction-chip").forEach((chip) => {
    chip.addEventListener("click", (e) => {
      e.stopPropagation(); // post cards are themselves clickable
      toggle(bar, chip.dataset.emoji);
    });
  });
  const add = bar.querySelector(".reaction-add");
  if (add) {
    add.addEventListener("click", (e) => {
      e.stopPropagation();
      showPicker(bar, add);
    });
  }
  const who = bar.querySelector(".reaction-who");
  if (who) {
    who.addEventListener("click", (e) => {
      e.stopPropagation();
      showWhoReacted(bar);
    });
  }
}

/** Binds every reaction bar inside `container`. Safe to call after each
 * re-render. */
export function attachReactionHandlers(container) {
  container.querySelectorAll(".reactions").forEach(bindBar);
}
